import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Col, Row, Tooltip, message } from "antd";
import IncomingWorkforceRequestsModal from "./IncomingWorkforceRequestsModal";

const getTransferRequest = (row) => {
  const sources = [row?.requestId, row?.companyTransfer];
  for (const value of sources) {
    if (!value) continue;
    if (Array.isArray(value)) {
      const found = value.find((item) => item && typeof item === "object");
      if (found) return found;
      continue;
    }
    if (typeof value === "object") return value;
  }
  return null;
};

const getCompanyId = (company) => {
  if (!company || typeof company !== "object") return "";
  return String(company._id || company.id || "");
};

const inferTransferKind = (row, advertId) => {
  const request = getTransferRequest(row);
  if (!request) return null;

  const currentCompanyId = String(localStorage.getItem("clientId") || "");
  const toId = getCompanyId(request.requestedToCompany);
  const fromId = getCompanyId(request.requestedFromCompany);
  if (currentCompanyId && currentCompanyId === toId) return "import";
  if (currentCompanyId && currentCompanyId === fromId) return "export";

  const destinationId = String(request.destinationAdvertId || "");
  if (advertId && destinationId) {
    return String(advertId) === destinationId ? "import" : "export";
  }
  return "import";
};

const hasRequestId = (requestId) => {
  if (requestId == null || requestId === "") return false;
  if (Array.isArray(requestId)) {
    return requestId.some((item) => item && typeof item === "object");
  }
  return typeof requestId === "object";
};

const isAcceptedCompanyTransferWithoutRequest = (row) => {
  if (hasRequestId(row?.requestId)) return false;
  const transfer = Array.isArray(row?.companyTransfer)
    ? row.companyTransfer.find((item) => item && typeof item === "object")
    : row?.companyTransfer;
  return Boolean(transfer && typeof transfer === "object" && transfer.isAccepted);
};

const getCandidateTransferBadges = (row, advertId) => {
  if (isAcceptedCompanyTransferWithoutRequest(row)) return [];

  const badges = [];
  if (row?.isCompanyTransferImport) badges.push("import");
  if (row?.isCompanyTransferExport) badges.push("export");
  if (badges.length) return badges;

  const kind = inferTransferKind(row, advertId);
  if (kind) badges.push(kind);
  return badges;
};

export const getCandidateDisplayName = (row) => {
  const name =
    row?.profileId?.name ||
    row?.candidateName ||
    row?.profileId?.userId?.name ||
    row?.name ||
    "";
  return typeof name === "string" ? name.trim() : "";
};

export const getTransferRequestStatus = (row) => {
  const request = getTransferRequest(row);
  if (!request) return null;
  if (request.isRejected) return "rejected";
  if (request.isAccepted) return "accepted";
  if (!request.isRejected) return "pending";
  return null;
};

export const getTransferBadgeTooltip = (row, kind) => {
  const request = getTransferRequest(row);
  const candidateName = getCandidateDisplayName(row);

  if (request?.isRejected) {
    return candidateName ? `Rejected by ${candidateName}` : "Rejected";
  }

  if (request?.isAccepted) {
    return candidateName ? `Accepted by ${candidateName}` : "Accepted";
  }

  if (!request) {
    return kind === "import" ? "Requested from" : "Sent to";
  }

  const company =
    kind === "import" ? request.requestedFromCompany : request.requestedToCompany;
  const tradingName =
    typeof company?.tradingName === "string" ? company.tradingName.trim() : "";
  const companyName =
    typeof company?.companyName === "string" ? company.companyName.trim() : "";
  const companyLabel = tradingName || companyName;
  const prefix = kind === "import" ? "Requested from" : "Sent to";
  return companyLabel ? `${prefix} ${companyLabel}` : prefix;
};

const transferCompanyLabel = getTransferBadgeTooltip;

export const TransferKindIcon = ({ kind, size = 15 }) =>
  kind === "import" ? (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="11.2" cy="4.4" r="1.7" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M8.6 13.1c.35-2.15 1.25-3.25 2.6-3.25 1.35 0 2.25 1.1 2.6 3.25"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path
        d="M1.4 7.2h4.3M4.15 5.45 5.9 7.2 4.15 8.95"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="4.8" cy="4.4" r="1.7" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M2.2 13.1c.35-2.15 1.25-3.25 2.6-3.25 1.35 0 2.25 1.1 2.6 3.25"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path
        d="M9.3 7.2h4.3M12.05 5.45 13.8 7.2l-1.75 1.75"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

/** Toolbar: same glyph as row badges, plain line icon like other table actions. */
export const WorkforceTransferToolbarIcon = ({ ready = true, kind = "export" }) => (
  <span
    className="workforce-transfer-toolbar-icon"
    style={{
      color: ready ? "#1638B1" : "#A7AEBB",
      cursor: ready ? "pointer" : "not-allowed",
    }}
    aria-hidden="true"
  >
    <TransferKindIcon kind={kind} size={24} />
  </span>
);

const hasHardReview = (candidate) => {
  const progress = candidate?.progress;
  const lastState =
    Array.isArray(progress) && progress.length
      ? progress[progress.length - 1]?.state
      : null;
  return lastState !== null && lastState !== undefined && Number(lastState) >= 3;
};

export const CandidateTransferBadges = ({ row, advertId }) => {
  const transferRequestStatus = getTransferRequestStatus(row);
  const badges = getCandidateTransferBadges(row, advertId);
  if (!badges.length) return null;

  return (
    <span className="candidate-transfer-badges">
      {badges.map((kind) => {
        const label = transferCompanyLabel(row, kind);
        return (
          <Tooltip
            key={kind}
            title={label}
            overlayClassName="transfer-badge-tooltip"
            placement="top"
            destroyTooltipOnHide
            mouseEnterDelay={0.1}
          >
            <span
              className={`transfer-kind-badge ${kind}${
                transferRequestStatus ? ` ${transferRequestStatus}` : ""
              }`}
              aria-label={label}
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={(e) => e.stopPropagation()}
              onMouseLeave={(e) => e.stopPropagation()}
            >
              <TransferKindIcon kind={kind} />
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
};

const WorkforceRequestButton = ({
  advertId,
  selectedIds = [],
  candidates = [],
  idField = "appliedJob",
  onSuccess,
}) => {
  const [visible, setVisible] = useState(false);
  const selected = candidates.filter((item) =>
    selectedIds.some((id) => String(item?.[idField]) === String(id))
  );
  const hasSelected = selectedIds.length > 0;
  const ready =
    hasSelected && selected.length === selectedIds.length && selected.every(hasHardReview);

  const open = () => {
    if (!hasSelected) return;
    if (!ready) {
      message.warning("Select a candidate who has reached Hard Review.");
      return;
    }
    setVisible(true);
  };

  return (
    <Col
      className="candidate_report_col cursor_pointer workforce-transfer-action"
      style={{ marginRight: "12px" }}
      onClick={hasSelected ? open : null}
    >
      <Tooltip
        title={
          !hasSelected
            ? "Select candidate(s) first"
            : !ready
            ? "Select a candidate who has reached Hard Review"
            : "Workforce Request"
        }
      >
        <Row align="middle" gutter={4} className="workforce-transfer-action-row">
          <Col>
            <WorkforceTransferToolbarIcon ready={ready} />
          </Col>
        </Row>
      </Tooltip>
      {typeof document !== "undefined" &&
        createPortal(
          <IncomingWorkforceRequestsModal
            visible={visible && hasSelected}
            onClose={() => setVisible(false)}
            advertId={advertId}
            applicationIds={selectedIds}
            onTransferSuccess={() => {
              setVisible(false);
              onSuccess?.();
            }}
          />,
          document.body
        )}
    </Col>
  );
};

export default WorkforceRequestButton;
